import { Box } from '@mui/material';
import FlexBox from 'Components/FlexBox';
import { ThemeTypography } from 'DesignSystem';
import ItemIssue from 'DesignSystem/DataDisplay/ItemIssue/ItemIssue';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { IGeoDataIssue } from 'FeatureModules/DataImport/types/dataset.types';
import { IPlot } from 'FeatureModules/Site/types/plot.types';
import React, { FC } from 'react';
import { formatAndRoundNumber } from 'utils/number.utils';

interface IPlotTitleProps {
  plot: IPlot;
  warnings?: IGeoDataIssue[];
  errors?: IGeoDataIssue[];
}

const PlotTitle: FC<IPlotTitleProps> = ({ plot, errors = [], warnings = [] }) => {
  return (
    <FlexBox>
      <Box bgcolor="white" p="4px" borderRadius="6px">
        <Icon name="plot-placeholder" color="blue-ice" size="xx-large" />
      </Box>

      <Box ml={2} width="calc(100% - 48px)" display="flex" flexDirection="column">
        <FlexBox
          maxWidth="100%"
          flexDirection="column"
          justifyContent="start"
          alignItems="flex-start"
        >
          <FlexBox width="100%" gap={1}>
            <ThemeTypography variant="ITEM_TITLE" autoOverflow>
              {plot.externalId ? `${plot.externalId}` : `No ID`}
            </ThemeTypography>
            {plot.title && (
              <>
                <ThemeTypography variant="BODY_MEDIUM" color="GRAY_80" autoOverflow>
                  |
                </ThemeTypography>
                <ThemeTypography variant="BODY_MEDIUM" color="GRAY_80" autoOverflow>
                  {plot.title}
                </ThemeTypography>
              </>
            )}

            {!!errors.length && <ItemIssue issues={errors} severity="error" itemName="Plot" />}

            {!!warnings.length && (
              <ItemIssue issues={warnings} severity="warning" itemName="Plot" />
            )}
          </FlexBox>

          <FlexBox gap={1} width="100%">
            <FlexBox gap={0.5}>
              <Icon name="tag-filled" size="small" color="gray-60" />
              <ThemeTypography variant="LABEL_INPUT" autoOverflow>
                Farm plot
              </ThemeTypography>
              <ThemeTypography variant="BODY_MEDIUM" color="GRAY_80" autoOverflow>
                ({plot.feature.geometry.type})
              </ThemeTypography>
            </FlexBox>

            <FlexBox maxWidth="100%" gap={0.5}>
              <Icon name="raw-material" size="small" color="GRAY_80" />
              <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80" autoOverflow>
                {plot.farm.rawMaterial.title}
              </ThemeTypography>
            </FlexBox>

            <FlexBox maxWidth="100%" gap={0.5}>
              <Icon name="farm" size="small" color="GRAY_80" />
              <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80" autoOverflow>
                {formatAndRoundNumber(plot.sizeHa || 0, 2)} ha
              </ThemeTypography>
            </FlexBox>

            <FlexBox maxWidth="calc(100% - 120px)" gap={0.5}>
              <Icon name="pin" size="small" color="GRAY_80" />
              <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80" autoOverflow>
                {plot.farm.title} {plot.farm.externalId ? `(${plot.farm.externalId})` : ''}
              </ThemeTypography>
            </FlexBox>

            <FlexBox maxWidth="calc(100% - 120px)" gap={0.5}>
              <Icon name="location" size="small" color="GRAY_80" />
              <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80" autoOverflow>
                {plot.farm.locationName}
              </ThemeTypography>
            </FlexBox>
          </FlexBox>
        </FlexBox>
      </Box>
    </FlexBox>
  );
};

export default PlotTitle;
